// frontend/src/pages/auth/Login.tsx
import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { toast } from 'react-toastify';
import api from '../../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { login, error: authError } = useAuth();

  // In Login component
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      setError(null);
      setLoading(true);
      
      try {
        // Attempt to login
        await login(email, password);
        
        // If successful, the login function from AuthContext will handle redirection
      } catch (err: any) {
        // Check for verification error specifically
        if (err.response?.status === 401 && 
            err.response?.data?.message === "Please verify your email before logging in") {
          
          console.log('Email verification required');
          
          // Store the email for the verification page
          localStorage.setItem('pendingVerificationEmail', email);
          
          // Use direct window.location.href for more reliable navigation
          window.location.href = '/verification-required';
          return; // Early return to prevent further error handling
        }
        
        // For other errors, throw them to be caught by the outer catch
        throw err;
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      const errorMessage = err.response?.data?.message || 'Failed to log in';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  

  // Show authError if present (e.g., verification required)
  useEffect(() => {
    if (authError) {
      setError(authError);
      if (authError === 'Please verify your email before logging in') {
        toast.info('Please check your email to verify your account.', {
          position: 'top-right',
          autoClose: 5000
        });
      }
    }
  }, [authError]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4">
              <Alert
                type="error"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
            />

            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
            />

            <div className="flex items-center justify-end">
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-pink-600 hover:text-pink-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Sign in
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or
                </span>
              </div>
            </div>

            <SocialLoginButtons className="mt-6" />

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium text-pink-600 hover:text-pink-500">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;